import { Component, OnInit, OnDestroy, AfterViewInit, Input, EventEmitter, Output } from '@angular/core';
import { ManageService } from '../../../../services/manage.service';
import { CommonService } from '../../../../services';
import { Router, ActivatedRoute } from '@angular/router';
import { combineLatest, Subject, throwError } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { GetCSPSubscription, KeyValuePair } from '../../../../models/csp.model';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DeleteConfirmComponent } from '../../../../popup-collection/delete-confirm/delete-confirm.component';
import { Location } from '@angular/common';

@Component({
  selector: 'app-budget-and-alerts',
  templateUrl: './budget-and-alerts.component.html',
  styleUrls: ['./budget-and-alerts.component.css']
})

export class BudgetAndAlertsComponent implements OnInit, OnDestroy {

  @Input() subscriptionId: string;

  @Input() tenantId: string;

  public currentScope: string;

  public refreshing = false;

  public loading = false;

  public azurePlan: GetCSPSubscription = {} as GetCSPSubscription;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  public scopeList = [] as KeyValuePair[];



  public budgetTableDataSrc: BudgeticElement[] = [];
  public displayedColumns: string[] = [
    'name',
    'resetPeriod',
    'createDate',
    'expDate',
    'budget',
    'currentSpend',
    'progress',
    'delete'
  ];

  public budgetAndAlertsForm = this.fb.group({
    scope: [null]
  });

  constructor(
    private manageService: ManageService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    public commonService: CommonService,
    private dialog: MatDialog,
    private location: Location
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.activatedRoute.paramMap,
      this.manageService.currentExistingCSPSubscriptions
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([param, subs]) => {
        this.currentScope = param.get('scopeForBudget');

        if (subs && subs.length) {
          this.azurePlan = subs.find(s => s.baseSubscription.out_subscription_id == this.subscriptionId).baseSubscription;
        }

        if (this.azurePlan && this.azurePlan.out_charge_type) {
          switch (this.azurePlan.out_charge_type) {
            case 'USAGE':
              this.scopeList.push({
                id: `${this.azurePlan.out_subscription_id} - ${this.azurePlan.out_sub_description}`,
                value: this.azurePlan.out_subscription_id
              } as KeyValuePair);
              this.scopeList.unshift({
                id: 'Tenancy Level',
                value: this.tenantId
              } as KeyValuePair)
              
              if (this.currentScope != this.tenantId) {
                this.router.navigateByUrl('/home');
              }

              this.budgetAndAlertsForm.get('scope').setValue(this.tenantId);
              this.loadBudgetAndAlertsTable(this.tenantId)
              break;
            case 'MODN':
              this.manageService
                .GetAzureEntitlements(this.tenantId.toLocaleLowerCase(), this.azurePlan.out_subscription_id)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((result: any) => {
                  this.scopeList = result.map(r => {
                    return {
                      id: `${r.entitlementId} - ${r.friendlyName}`,
                      value: r.entitlementId
                    } as KeyValuePair
                  })
                  this.scopeList.unshift({
                    id: 'Tenancy Level',
                    value: this.tenantId
                  } as KeyValuePair)

                  const found = this.scopeList.find(s => s.value == this.currentScope)

                  if (!found) {
                    this.router.navigateByUrl('/home');
                  }

                  this.budgetAndAlertsForm.get('scope').setValue(found.value);
                  this.loadBudgetAndAlertsTable(found.value)
                },  (error: any) => {
                  this.commonService.implementToast(error, 'Error');
                });
              break;
          }
        }

      })

  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private loadBudgetAndAlertsTable(scope: string): void {
    this.loading = true;
    this.manageService
      .GetBudgetAlertList(this.tenantId,  scope == this.tenantId ? '' : scope)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result: any) => {
        if (result && result.value) {
          this.processBudgetAlertData(result.value)
        }
      },  (error: any) => {
        this.commonService.implementToast('Loading Budget & Alerts Error', 'Error');
      })
      .add(() => {
        this.loading = false;
        this.refreshing = false;
      });
  }

  public createBudget(): void {
    this.router.navigate([`createBudget/${this.budgetAndAlertsForm.get('scope').value}`], { relativeTo: this.activatedRoute });
  }

  private processBudgetAlertData(data: any): void {
    this.budgetTableDataSrc = data.map(record => {
      return {
        name: record.name,
        resetPeriod: record.properties.timeGrain,
        createDate: record.properties.timePeriod.startDate,
        expDate: record.properties.timePeriod.endDate,
        budget: record.properties.amount,
        currentSpend: record.properties.currentSpend.amount,
        progress: record.properties.currentSpend.amount/record.properties.amount
      } as BudgeticElement
    })
  }

  public budgetAlertsDetailView(budget: any): void {
    this.router.navigate([`BudgetAlertDetails/${budget.name}`], { relativeTo: this.activatedRoute });
  }

  public switchScope(event): void {
    const scope = event.value
    const prefix = 'BudgetAlerts/';
    const cururl = this.location.path().replace(prefix + this.currentScope, prefix + scope);
    this.currentScope = scope;
    this.location.go(cururl);
    this.loadBudgetAndAlertsTable(this.currentScope);
  }

  public deleteBudget(budgetName: string): void {
    this.dialog.open(DeleteConfirmComponent, {
      width: '500px',
      height:'175px',
      panelClass: 'mat-dialog-wrapper'
    }).afterClosed().subscribe((ok) => {
      if (ok) {
        this.manageService
          .deleteBudget(this.tenantId, budgetName)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((result: any) => {
            this.manageService.refetchBudgetList$.next();
          },  (error: any) => {
            this.commonService.implementToast(error, 'Error');
          });
      }
    });
  }

  public refresh(): void {
    if (!this.loading) {
      this.refreshing = true;
      this.loadBudgetAndAlertsTable(this.currentScope)
    }
  }

  public close(): void {
    this.manageService.sendCurrentSoftwareSubscription(null);
    this.manageService.backToTenentPage(this.tenantId);
  }
}

export interface BudgeticElement {
  name: string;
  resetPeriod: string;
  createDate: string;
  expDate: string;
  budget: number;
  currentSpend: number;
  progress: number;
}

